import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Star,
  Edit,
  Trash2,
  CopyPlus,
  RotateCw,
  Filter,
  X,
  Download,
  Wand2,
  Sparkles,
  Loader2,
  HelpCircle,
  Settings2,
  Info,
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { toast } from "sonner";
import { useUser } from "../../../../context/UserContext";
import { useCreatePermissions } from "../../../../hooks/useCreatePermissions";
import { useHat } from "../../../../context/HatContext";
import { getConnectorInfo } from "../../../../service/apiService";
import DuplicateVAForm from "./DuplicateVAForm";
import MobileVAListView from "./MobileVAListView";
import DepartmentFilterButtons from "./DepartmentFilterButtons";
import LockButton from "../../../Lock/LockButton";
import LockBadge from "../../../Lock/LockBadge";

// Define Hat/VA type
interface Hat {
  id?: string;
  hat_name: string;
  // Admin lock. Only ever set on rows the viewer locked themselves -- the API
  // hides everyone else's locked VAs from this list entirely.
  locked_by?: string | null;
  locked_at?: string | null;
  department?: string;
  summary: string;
  initial_prompt?: string;
  directions: string;
  is_public: boolean;
  feature_keys: {
    image_creation: { enabled: boolean };
    internet_browsing: { enabled: boolean };
    kyva_llm: { enabled: boolean };
    kyva_connectors: { enabled: boolean; connector_ids?: string[] };
    include_knowledge_base: {
      enabled: boolean;
      knowledge_base_ids: string[];
    };
  };
  favorites?: {[key: string]: boolean | { BOOL: boolean }};
  selected_icon?: File | null;
  created_at?: string;
  updated_at?: string;
  userId?: string;
  user_id?: string;
  isShared?: boolean;
}

// Connector as returned by /connectors/info -- only the fields the filter needs.
interface ConnectorOption {
  id: string;
  name: string;
  type: string;
  status?: string;
}

interface VAListViewProps {
  onCreateClick: () => void;
  onEditClick: (va: Hat) => void;
  onDuplicateClick?: (va: Hat) => void; 
  onClose?: () => void;
}

const VAListView: React.FC<VAListViewProps> = ({
  onCreateClick,
  onEditClick,
  onDuplicateClick,
  onClose  
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVA, setSelectedVA] = useState<Hat | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isMobileView, setIsMobileView] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempFilter, setTempFilter] = useState<string>("all");
  const [connectors, setConnectors] = useState<ConnectorOption[]>([]);
  const [tempConnectorFilter, setTempConnectorFilter] = useState<string | null>(null);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vaToDelete, setVaToDelete] = useState<Hat | null>(null);
  const [showAIModifyModal, setShowAIModifyModal] = useState(false);
  const [vaToModify, setVaToModify] = useState<Hat | null>(null);
  const [aiInstructions, setAiInstructions] = useState("");
  const [isAIModifying, setIsAIModifying] = useState(false);
  const [showAIHelp, setShowAIHelp] = useState(false);
  // VA whose created/updated details the tooltip is currently showing.
  const [infoVA, setInfoVA] = useState<Hat | null>(null);

  const {
    hatsList: vaList,
    fetchHatsListWithFilter,
    fetchHatsList,
    loading,
    listLoading,
    deleteHatById: deleteVA,
    toggleFavorite: toggleVAFavorite,
    setSelectedHat,
    currentFilter,
    updateCurrentFilter,
    currentDepartmentFilter,
    updateCurrentDepartmentFilter,
    currentConnectorFilter: connectorFilter,
    updateCurrentConnectorFilter: setConnectorFilter,
    modifyHatWithLLM,
  } = useHat();
  
  const { userId, userDetails } = useUser();
  const isAdmin = userDetails?.role !== 'user';
  const { canCreateVA } = useCreatePermissions();

  // Detect mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  // Create a stable reference to prevent re-renders
const fetchData = React.useCallback(() => {
  if (!userId) return;

  const loadVAs = async () => {
    try {
      setIsFilterLoading(true);
      if (isAdmin && currentFilter === 'all') {
        await fetchHatsList();
      } else {
        await fetchHatsListWithFilter(currentFilter);
      }
    } catch (error) {
      console.error("Error loading VAs:", error);
      toast.error("Failed to load Virtual Assistants");
    } finally {
      setIsFilterLoading(false);
    }
  };

  loadVAs();
}, [userId, currentFilter, isAdmin]); 

// Use the stable reference in useEffect
useEffect(() => {
  fetchData();
}, [fetchData]);

  // Load connectors once so the filter modal can list them
  useEffect(() => {
    const loadConnectors = async () => {
      try {
        const response = await getConnectorInfo();
        setConnectors(response.connectors || []);
      } catch (error) {
        console.error("Error loading connectors:", error);
      }
    };

    loadConnectors();
  }, []);

  // Helper function to check if a hat is favorited
  const isVAFavorite = (va: Hat) => {
    if (!va.favorites || !userId) return false;
    
    const favoriteValue = va.favorites[userId];
    
    if (typeof favoriteValue === 'object' && favoriteValue?.BOOL !== undefined) {
      return favoriteValue.BOOL === true;
    }
    
    return favoriteValue === true;
  };
  
const filteredVAs = React.useMemo(() => {
  let filtered = vaList;

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter((va: Hat) => {
      const hatName = va.hat_name?.toLowerCase() || '';
      const summary = va.summary?.toLowerCase() || '';
      return hatName.includes(query) || summary.includes(query);
    });
  }

  // Apply department filter
  if (currentDepartmentFilter) {
    filtered = filtered.filter((va: Hat) => {
      const vaDepartment = va.department;
      
      // Handle various department field states
      if (!vaDepartment || vaDepartment.trim() === '') {
        return false; // VA has no department, doesn't match selected department
      }
      
      return vaDepartment.trim() === currentDepartmentFilter.trim();
    });
  }

  // Apply connector filter -- keep only VAs that use the selected connector
  if (connectorFilter) {
    filtered = filtered.filter((va: Hat) =>
      (va.feature_keys?.kyva_connectors?.connector_ids || []).includes(connectorFilter)
    );
  }

  return filtered;
}, [vaList, searchQuery, currentDepartmentFilter, connectorFilter]);
  
  // Handle filter modal open
  const handleFilterClick = () => {
    setTempFilter(currentFilter);
    setTempConnectorFilter(connectorFilter);
    setShowFilterModal(true);
  };

  const handleApplyFilter = async () => {
  if (tempConnectorFilter !== connectorFilter) {
    setConnectorFilter(tempConnectorFilter);
  }

  if (tempFilter !== currentFilter) {
    setShowFilterModal(false);
    
    try {
      updateCurrentFilter(tempFilter);
      setSearchQuery("");
      // The context's useEffect will handle the data loading
      // No need to call fetchHatsListWithFilter here
    } catch (error) {
      console.error("Error applying filter:", error);
      toast.error("Failed to apply filter");
    }
  } else {
    setShowFilterModal(false);
  }
};

  const handleClearFilters = async () => {
  const defaultFilter = "all";
  setTempConnectorFilter(null);
  if (connectorFilter !== null) {
    setConnectorFilter(null);
  }

  if (currentFilter !== defaultFilter) {
    setIsFilterLoading(true);
    setShowFilterModal(false);
    
    try {
      setTempFilter(defaultFilter);
      updateCurrentFilter(defaultFilter);
      setSearchQuery("");
      updateCurrentDepartmentFilter(null); 
    } catch (error) {
      console.error("Error clearing filters:", error);
      toast.error("Failed to clear filters");
    } finally {
      setIsFilterLoading(false);
    }
  } else {
    setTempFilter(defaultFilter);
    setShowFilterModal(false);
  }
};
  
// Handle department filter change
const handleDepartmentChange = (departmentName: string | null) => {
  updateCurrentDepartmentFilter(departmentName);
};

// Clear all filters function
const handleClearAllFilters = () => {
  setSearchQuery("");
  updateCurrentDepartmentFilter(null);
  setConnectorFilter(null);
  setTempConnectorFilter(null);
  if (currentFilter !== "all") {
    updateCurrentFilter("all");
  }
};

const handleExportVA = (va: Hat) => {
  try {
    // Create export data with exact field mapping
    const exportData = {
      virtual_assistant_name: va.hat_name || "",
      summary: va.summary || "",
      initial_prompt: va.initial_prompt || "",
      instructions: va.directions || ""
    };
    
    // Create JSON blob
    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${va.hat_name || 'virtual_assistant'}_export.json`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success("Virtual Assistant exported successfully!");
  } catch (error) {
    console.error("Error exporting VA:", error);
    toast.error("Failed to export Virtual Assistant");
  }
};

// Handle VA selection for chat
const handleVASelection = (va: Hat) => {
  if (va.id) {
    setSelectedHat(va as any);
    toast.success(`Selected Virtual Assistant: ${va.hat_name}`);
    // Close the modal after successful selection
    if (onClose) {
      onClose();
    }
  } else {
    toast.error("Cannot select: Missing VA ID");
  }
};

// Check if any filters are active
const hasActiveFilters = () => {
  return currentFilter !== 'all' || currentDepartmentFilter !== null || connectorFilter !== null || searchQuery.trim() !== '';
};

  // Handle tooltip display
  const handleIconMouseEnter = (e: React.MouseEvent, iconType: string) => {
    if (!confirmDeleteId) {
      setHoveredIcon(iconType);
      updateTooltipPosition(e);
    }
  };
  
  const handleIconMouseLeave = () => {
    setHoveredIcon(null);
  };
  
  const handleIconMouseMove = (e: React.MouseEvent) => {
    if (hoveredIcon && !confirmDeleteId) {
      updateTooltipPosition(e);
    }
  };
  
  const updateTooltipPosition = (e: React.MouseEvent) => {
    setTooltipPosition({
      top: e.clientY + 10,
      left: e.clientX + 10
    });
  };
  
  const handleShare = (va: Hat, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedVA(va);
    setShowShareModal(true);
  };
  
  const handleDeleteClick = (va: Hat, e?: React.MouseEvent) => {
  if (e) e.stopPropagation();
  setHoveredIcon(null);
  if (va.id) {
    setVaToDelete(va);
    setShowDeleteModal(true);
  } else {
    toast.error("Cannot delete: Missing VA ID");
  }
};

  // ISO timestamps out of DynamoDB; anything missing or unparseable reads
  // "Unknown" rather than "Invalid Date".
  const formatAuditDate = (value?: string) => {
    if (!value) return "Unknown";
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? "Unknown" : parsed.toLocaleString();
  };

  const handleDuplicateVA = (va: Hat, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (onDuplicateClick) {
      onDuplicateClick(va);
    }
  };

  const handleAIModifyClick = (va: Hat, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setVaToModify(va);
    setAiInstructions("");
    setShowAIHelp(false);
    setShowAIModifyModal(true);
  };

  const handleAIModifySubmit = async () => {
    if (!vaToModify?.id || !aiInstructions.trim()) return;

    setIsAIModifying(true);
    try {
      const result = await modifyHatWithLLM(vaToModify.id, aiInstructions.trim());
      toast.success("Virtual Assistant modified successfully with AI!");
      setShowAIModifyModal(false);
      setVaToModify(null);
      setAiInstructions("");
    } catch (err: any) {
      console.error("Error modifying VA with AI:", err);
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to modify Virtual Assistant with AI. Please try again.");
    } finally {
      setIsAIModifying(false);
    }
  };
  
  const handleDelete = async () => {
  if (!vaToDelete?.id) {
    toast.error("Error: No Virtual Assistant selected for deletion");
    return;
  }
  
  setIsDeleting(true);
  try {
    await deleteVA(vaToDelete.id);
    setShowDeleteModal(false);
    setVaToDelete(null);
    // Let deleteVA in context handle the refresh
  } catch (err: any) {
    console.error("Error deleting hat:", err);
    if (err?.response?.data?.detail) {
      toast.error(err.response.data.detail);
    } else if (err?.message) {
      toast.error(err.message);
    } else {
      toast.error("Failed to delete Virtual Assistant");
    }
  } finally {
    setIsDeleting(false);
  }
};
  
  const handleFavoriteToggle = async (va: Hat, e?: React.MouseEvent) => {
  if (e) e.stopPropagation();
  if (!va.id) {
    toast.error("Cannot toggle favorite: Missing VA ID");
    return;
  }
  
  try {
    await toggleVAFavorite(va.id);
    // Let toggleVAFavorite in context handle the refresh
  } catch (error) {
    console.error("Error toggling favorite:", error);
    toast.error("Failed to update favorite status");
  }
};
  
  const canEditVA = (va: Hat): boolean => {
    if (isAdmin) return true;
    const currentUserId = userId?.toLowerCase() || "";
    const isOwner = (va as any).user_id === currentUserId || (va as any).userId === currentUserId;
    if (isOwner) return true;
    const isManager = (va as any).managers && Array.isArray((va as any).managers)
      ? (va as any).managers.includes(currentUserId)
      : false;
    return isManager;
  };

  const getVAType = (va: Hat, currentUserId: string, currentFilterType?: string) => {
    // Always check actual ownership first
    const isOwner = (va as any).user_id === currentUserId || (va as any).userId === currentUserId;
    const isManager = (va as any).managers && Array.isArray((va as any).managers)
      ? (va as any).managers.includes(currentUserId)
      : false;

    if (isOwner) {
      return "My VA";
    } else if (isManager) {
    return "Managed VA";
    } else if ((va as any).is_public) {
      return "Public VA";
    } else {
      return "Shared VA";
    }
  };

  // Get filter display text
  const getFilterDisplayText = () => {
    switch (currentFilter) {
      case 'favorites':
        return 'Favorites';
      case 'my_hats':
        return 'My VAs';
      case 'public_hats':
        return 'Public VAs';
      case 'managed_hats':  
        return 'Managed VAs';  
      case 'shared_hats':  
        return 'Shared VAs';  
      case 'all':
        return 'All VAs';
      default:
        return 'All VAs';
    }
  };
  
  // Render mobile view if screen size is small
  if (isMobileView) {
    return (
      <MobileVAListView
        onCreateClick={onCreateClick}
        onEditClick={onEditClick}
        onDuplicateClick={onDuplicateClick}
        onDeleteClick={handleDeleteClick}
        onFavoriteToggle={handleFavoriteToggle}
        onClose={onClose} 
        vaList={filteredVAs}
        loading={loading}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        isAdmin={isAdmin}
        userId={userId}
        connectors={connectors}
        connectorFilter={connectorFilter}
        onConnectorFilterChange={setConnectorFilter}
      />
    );
  }
  
  // Original desktop view
  return (
    <>
      <div className="mx-auto w-full max-w-screen-xl p-4">
        {/* Main VA List View */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Header with search, filters and create button */}
          <div className="p-4 border-b">
            {/* Top row - Search and Create button */}
            <div className="flex items-center justify-between mb-4">
              <div className="relative flex-1 max-w-md">
               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 bg-white text-gray-900 border border-gray-200 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder="Search Virtual Assistants"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
           </div>

           {canCreateVA && (
           <button
             onClick={onCreateClick}
             className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center ml-4"
           >
           <Plus className="h-4 w-4 mr-2" />
             Create New VA
           </button>
           )}
         </div>

         {/* Second row - VA Type Filter and Clear All */}
         <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
           {/* VA Type Filter Button */}
            <button
              onClick={handleFilterClick}
              disabled={isFilterLoading}
              className={`flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-500 min-w-[140px] ${
                isFilterLoading ? 'opacity-50 cursor-not-allowed' : ''
             }`}
             >
            {isFilterLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-violet-600 mr-2"></div>
                 <span>Loading...</span>
              </div>
            ) : (
              <>
              <span>{getFilterDisplayText()}</span>
              <Filter size={14} className="ml-2 text-gray-400" />
            </>
          )}
        </button>

        {/* Active connector filter chip */}
        {connectorFilter && (
          <span className="inline-flex items-center px-3 py-1.5 text-sm rounded-full bg-violet-50 text-violet-700 border border-violet-200">
            <Settings2 size={14} className="mr-1.5" />
            {connectors.find((c) => c.id === connectorFilter)?.name || "Connector"}
            <button
              onClick={() => {
                setConnectorFilter(null);
                setTempConnectorFilter(null);
              }}
              className="ml-2 text-violet-400 hover:text-violet-600"
              title="Remove connector filter"
            >
              <X size={12} />
            </button>
          </span>
        )}
      </div>

      {/* Clear All Filters Button (only show when filters are active) */}
      {hasActiveFilters() && (
        <button
          onClick={handleClearAllFilters}
          className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md border border-gray-200"
        >
          <X size={14} className="mr-1" />
          Clear all
        </button>
      )}
    </div>

    {/* Third row - Department Filter Buttons (You.com style) */}
    <div className="border-t pt-4">
     <DepartmentFilterButtons
    selectedDepartment={currentDepartmentFilter}
    onDepartmentChange={handleDepartmentChange}
    className="pb-2"
   />
  </div>
  </div>
          
  {/* VA Table */}
  <div className="overflow-x-auto">
    <table className="w-full divide-y divide-gray-200">
     <thead className="bg-gray-50">
  <tr>
    <th scope="col" className="w-1/4 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Name
    </th>
     <th scope="col" className="w-1/6 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Department
    </th>
    {!isAdmin && (
      <th scope="col" className="w-1/8 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Type
      </th>
    )}
    <th scope="col" className="flex-1 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Summary
    </th>
     <th scope="col" className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Actions
    </th>
  </tr>
</thead>
<tbody className="bg-white divide-y divide-gray-200">
 {loading || isFilterLoading ? (
  <tr>
    <td colSpan={isAdmin ? 4 : 5} className="px-3 py-4 text-center text-sm text-gray-500">
      <div className="flex items-center justify-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
        <span>{isFilterLoading ? "Applying filter..." : "Loading Virtual Assistants..."}</span>
      </div>
    </td>
  </tr>
) : filteredVAs.length === 0 ? (
  <tr>
    <td colSpan={isAdmin ? 4 : 5} className="px-3 py-4 text-center text-sm text-gray-500">
      {searchQuery || currentDepartmentFilter || connectorFilter
        ? "No Virtual Assistants match your search"
        : `No ${getFilterDisplayText().toLowerCase()} found`}
    </td>
  </tr>
  ) : (
 filteredVAs.map((va: Hat) => (
  <tr 
    key={va.id} 
    className="hover:bg-gray-50"
  >
    <td className="w-1/4 px-3 py-3 max-w-0">
  <div className="flex items-center gap-2 min-w-0">
    <span
      className="text-sm font-medium text-primary truncate cursor-pointer hover:underline transition-colors duration-150"
      title={`Click to select: ${va.hat_name || 'Untitled VA'}`}
      onClick={(e) => {
        e.stopPropagation(); // Prevent row click from triggering edit
        handleVASelection(va);
      }}
    >
      {va.hat_name || 'Untitled VA'}
    </span>
    {va.locked_by && (
      <LockBadge
        className="flex-shrink-0"
        label={
          va.locked_at
            ? `Locked on ${new Date(va.locked_at).toLocaleDateString()}`
            : "Locked"
        }
      />
    )}
  </div>
</td>
    <td className="w-1/6 px-3 py-3 max-w-0">
     <div className="text-sm text-gray-900 truncate" title={
       (() => {
     // Handle both missing department field and empty department field
     const department = va.department;

     // Check if department exists and is not empty
     if (department && typeof department === 'string' && department.trim() !== '') {
      return department;
    } else {
      return "No Department";
      }
      })()
     }>
      {(() => {
   // Handle both missing department field and empty department field
   const department = va.department;

   // Check if department exists and is not empty
   if (department && typeof department === 'string' && department.trim() !== '') {
    return department;
  } else {
    return "No Department";
    }
    })()}
  </div>
  </td>
    {!isAdmin && (
      <td className="w-1/8 px-3 py-3 max-w-0">
        <div className="text-sm text-gray-800 truncate">
          {getVAType(va, userId, currentFilter)}
        </div>
      </td>
    )}
    <td className="flex-1 px-3 py-3 max-w-0">
      <div className="text-sm text-gray-900 line-clamp-2" title={va.summary}>
        {va.summary}
      </div>
    </td>
  <td className="w-36 px-3 py-3 text-right text-sm font-medium">
  <div className="flex items-center justify-end space-x-1">
    {/* Favorite Button with Tooltip */}
    <button 
      onClick={(e) => handleFavoriteToggle(va, e)}
      className="p-1 rounded focus:outline-none text-gray-400 hover:text-gray-600"
      aria-label={isVAFavorite(va) ? "Remove from favorites" : "Add to favorites"}
      onMouseEnter={(e) => handleIconMouseEnter(e, isVAFavorite(va) ? "unfavorite" : "favorite")}
      onMouseLeave={handleIconMouseLeave}
      onMouseMove={handleIconMouseMove}
    >
      <Star 
        className={`h-3.5 w-3.5 ${isVAFavorite(va) ? "text-green-500 fill-current" : ""}`} 
      />
    </button>
    
    {/* Edit Button with Tooltip -- hidden when admin turned off VA creation */}
    {canCreateVA && (
    <button
      onClick={() => onEditClick(va)}
      className="p-1 text-blue-600 hover:text-blue-800 rounded focus:outline-none"
      aria-label="Edit Virtual Assistant"
      onMouseEnter={(e) => handleIconMouseEnter(e, "edit")}
      onMouseLeave={handleIconMouseLeave}
      onMouseMove={handleIconMouseMove}
    >
      <Edit className="h-3.5 w-3.5" />
    </button>
    )}

    {/* Duplicate Button -- hidden; handleDuplicateVA/DuplicateVAForm stay wired
        up so it can be brought back by un-commenting this block.
    <button 
      onClick={(e) => handleDuplicateVA(va, e)}
      className="p-1 text-violet-600 hover:text-violet-800 rounded focus:outline-none"
      aria-label="Duplicate Virtual Assistant"
      onMouseEnter={(e) => handleIconMouseEnter(e, "duplicate")}
      onMouseLeave={handleIconMouseLeave}
      onMouseMove={handleIconMouseMove}
    >
      <CopyPlus className="h-3.5 w-3.5" />
    </button>
    */}

    {/* Details Button -- who created/last updated this VA, and when */}
    <button
      type="button"
      onClick={(e) => e.stopPropagation()}
      className="p-1 text-gray-400 hover:text-gray-600 rounded focus:outline-none"
      aria-label="Virtual Assistant details"
      onMouseEnter={(e) => { setInfoVA(va); handleIconMouseEnter(e, "info"); }}
      onMouseLeave={() => { setInfoVA(null); handleIconMouseLeave(); }}
      onMouseMove={handleIconMouseMove}
    >
      <Info className="h-3.5 w-3.5" />
    </button>

     <button
    onClick={(e) => {
      e.stopPropagation();
      handleExportVA(va);
      }}
      className="p-1 text-green-600 hover:text-green-800 rounded focus:outline-none"
      aria-label="Export Virtual Assistant"
      onMouseEnter={(e) => handleIconMouseEnter(e, "export")}
      onMouseLeave={handleIconMouseLeave}
      onMouseMove={handleIconMouseMove}
     >
    <Download className="h-3.5 w-3.5" />
  </button>

    {/* AI Modify + Delete -- hidden when admin turned off VA creation */}
    {canCreateVA && (
    <>
    {/* AI Modify Button */}
    <button
      onClick={(e) => { e.stopPropagation(); if (canEditVA(va)) handleAIModifyClick(va, e); }}
      className={`p-1 rounded focus:outline-none ${canEditVA(va) ? "text-amber-500 hover:text-amber-700" : "text-gray-300 cursor-not-allowed"}`}
      aria-label={canEditVA(va) ? "Modify with AI" : "You don't have access to this VA"}
      onMouseEnter={(e) => handleIconMouseEnter(e, canEditVA(va) ? "ai-modify" : "ai-modify-disabled")}
      onMouseLeave={handleIconMouseLeave}
      onMouseMove={handleIconMouseMove}
    >
      <Wand2 className="h-3.5 w-3.5" />
    </button>

    {/* Delete Button */}
      <button 
       onClick={(e) => handleDeleteClick(va, e)}
       className="p-1 text-red-600 hover:text-red-800 rounded focus:outline-none"
       aria-label="Delete Virtual Assistant"
       onMouseEnter={(e) => handleIconMouseEnter(e, "delete")}
       onMouseLeave={handleIconMouseLeave}
       onMouseMove={handleIconMouseMove}
      >
   <Trash2 className="h-3.5 w-3.5" />
  </button>
    </>
    )}

    {/* Lock Button — admin only; renders nothing for regular users */}
    <LockButton
      resourceType="va"
      resourceId={va.id}
      resource={va}
      resourceName={va.hat_name}
      // Same owner fields the canEditVA/isOwner checks above use: rows come
      // back with user_id, older shapes with userId.
      ownerId={va.user_id || (va as any).userId}
      onChange={fetchData}
      className="p-1"
    />
  </div>
</td>
</tr>
 ))
)}
</tbody>
</table>
</div>
{/* Delete Confirmation Modal */}
<Transition appear show={showDeleteModal} as={Fragment}>
  <Dialog as="div" className="relative z-50" onClose={() => !isDeleting && setShowDeleteModal(false)}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-black bg-opacity-25" />
    </Transition.Child>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
            {/* Warning Icon */}
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            
            {/* Title */}
            <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 text-center mb-2">
              Delete Virtual Assistant
            </Dialog.Title>
            
            {/* Content */}
            <div className="mb-6 text-center">
              <p className="text-sm text-gray-500 mb-2">
                Are you sure you want to delete
              </p>
              <p className="text-sm font-medium text-gray-900 mb-3">
                "{vaToDelete?.hat_name}"?
              </p>
              <p className="text-sm text-red-600 font-medium">
                ⚠️ You will lose all data associated with this Virtual Assistant.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 justify-center">
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:opacity-50"
                onClick={() => {
                  setShowDeleteModal(false);
                  setVaToDelete(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:opacity-50"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </div>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>
  </div>

{/* AI Modify Modal */}
<Transition appear show={showAIModifyModal} as={Fragment}>
  <Dialog as="div" className="relative z-50" onClose={() => !isAIModifying && setShowAIModifyModal(false)}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-black bg-opacity-25" />
    </Transition.Child>

    <div className="fixed inset-0 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-10 h-10 bg-amber-50 rounded-full">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                    Modify with AI
                  </Dialog.Title>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {vaToModify?.hat_name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => !isAIModifying && setShowAIModifyModal(false)}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                disabled={isAIModifying}
              >
                <X size={20} />
              </button>
            </div>

            {/* Description */}
            <p className="text-sm text-gray-600 mb-4">
              Describe the changes you'd like to make to this Virtual Assistant. AI will interpret your instructions and apply the modifications.
            </p>

            {/* Instruction Input */}
            <div className="mb-4">
              <div className="flex items-center gap-1.5 mb-1.5">
                <label htmlFor="ai-instructions" className="block text-sm font-medium text-gray-700">
                  Instructions
                </label>
                <button
                  type="button"
                  onClick={() => setShowAIHelp(!showAIHelp)}
                  className="text-gray-400 hover:text-amber-500 transition-colors"
                  title="Click to see which fields AI can modify"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                </button>
              </div>
              {showAIHelp && (
                <div className="mb-2 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
                  <p className="font-medium text-gray-700 mb-1.5">AI can modify these fields:</p>
                  <ul className="space-y-1">
                    <li><span className="font-medium text-amber-600">Name</span> — Display name of the VA</li>
                    <li><span className="font-medium text-amber-600">Summary</span> — Brief description shown in the list</li>
                    <li><span className="font-medium text-amber-600">Initial Prompt</span> — First message sent to users</li>
                    <li><span className="font-medium text-amber-600">Instructions</span> — Behavior rules and guidelines</li>
                  </ul>
                </div>
              )}
              <textarea
                id="ai-instructions"
                value={aiInstructions}
                onChange={(e) => setAiInstructions(e.target.value)}
                placeholder="e.g., Make the tone more professional, add instructions for handling customer complaints, change the name to..."
                className="w-full h-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none placeholder:text-gray-400"
                disabled={isAIModifying}
                autoFocus
              />
            </div>

            {/* AI Processing State */}
            {isAIModifying && (
              <div className="mb-4 flex items-center gap-2 px-3 py-2.5 bg-amber-50 rounded-lg border border-amber-100">
                <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
                <span className="text-sm text-amber-700">AI is modifying your Virtual Assistant...</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:opacity-50"
                onClick={() => {
                  setShowAIModifyModal(false);
                  setVaToModify(null);
                  setAiInstructions("");
                }}
                disabled={isAIModifying}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleAIModifySubmit}
                disabled={isAIModifying || !aiInstructions.trim()}
              >
                {isAIModifying ? (
                  <div className="flex items-center">
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                    Applying...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Wand2 className="h-4 w-4 mr-2" />
                    Apply Changes
                  </div>
                )}
              </button>
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>

   {/* Tooltip - only show when not in delete confirmation mode */}
        {hoveredIcon && !confirmDeleteId && (
          <div 
            className="fixed bg-white border border-gray-200 px-2 py-1 text-xs shadow-md z-50"
            style={{ 
              top: tooltipPosition.top + 'px', 
              left: tooltipPosition.left + 'px',
              fontSize: '12px',
              borderRadius: '2px',
              // The details panel is wide and its icon lives in the last
              // column, so open it to the left of the cursor instead.
              ...(hoveredIcon === "info"
                ? { transform: 'translateX(calc(-100% - 20px))' }
                : {}),
            }}
          >
            {hoveredIcon === "favorite" && "Add to favorites"}
            {hoveredIcon === "unfavorite" && "Remove from favorites"}
            {hoveredIcon === "edit" && "Edit"}
            {hoveredIcon === "duplicate" && "Duplicate"}
            {hoveredIcon === "info" && infoVA && (
              <div className="space-y-1 whitespace-nowrap py-0.5">
                <div>
                  <span className="text-gray-500">Created:</span>{" "}
                  <span className="text-gray-900">{formatAuditDate(infoVA.created_at)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Created by:</span>{" "}
                  <span className="text-gray-900">{infoVA.user_id || infoVA.userId || "Unknown"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Last updated:</span>{" "}
                  <span className="text-gray-900">{formatAuditDate(infoVA.updated_at)}</span>
                </div>
              </div>
            )}
            {hoveredIcon === "export" && "Export/Download"}
            {hoveredIcon === "ai-modify" && "Modify with AI"}
            {hoveredIcon === "ai-modify-disabled" && "You don't have access to this VA"}
            {hoveredIcon === "delete" && "Delete"}
          </div>
        )}
        {showShareModal && (
          <p className="text-center text-gray-500 mt-4">
            Share modal would be implemented here
          </p>
        )}
      </div>

      {/* Filter Modal - FIXED: Updated order to match MobileVAListView */}
      <Transition appear show={showFilterModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowFilterModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex justify-between items-center mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Filter Virtual Assistants
                    </Dialog.Title>
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="flex items-center text-sm font-medium text-gray-700 mb-3">
                        <Star size={16} className="mr-2 text-yellow-500" />
                        Filters
                      </h4>
                      <div className="space-y-2">
  <label className="flex items-center">
    <input
      type="radio"
      name="filter"
      value="all"
      checked={tempFilter === 'all'}
      onChange={(e) => setTempFilter(e.target.value)}
      className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
    />
    <span className="ml-2 text-sm text-gray-700">All VAs</span>
  </label>
  <label className="flex items-center">
    <input
      type="radio"
      name="filter"
      value="favorites"
      checked={tempFilter === 'favorites'}
      onChange={(e) => setTempFilter(e.target.value)}
      className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
    />
    <span className="ml-2 text-sm text-gray-700">Favorites</span>
  </label>
  <label className="flex items-center">
    <input
      type="radio"
      name="filter"
      value="my_hats"
      checked={tempFilter === 'my_hats'}
      onChange={(e) => setTempFilter(e.target.value)}
      className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
    />
    <span className="ml-2 text-sm text-gray-700">My VAs</span>
  </label>
  {/* FILTER OPTIONS */}
  <label className="flex items-center">
    <input
      type="radio"
      name="filter"
      value="managed_hats"
      checked={tempFilter === 'managed_hats'}
      onChange={(e) => setTempFilter(e.target.value)}
      className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
    />
    <span className="ml-2 text-sm text-gray-700">Managed VAs</span>
  </label>
  <label className="flex items-center">
    <input
      type="radio"
      name="filter"
      value="shared_hats"
      checked={tempFilter === 'shared_hats'}
      onChange={(e) => setTempFilter(e.target.value)}
      className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
    />
    <span className="ml-2 text-sm text-gray-700">Shared VAs</span>
  </label>
  <label className="flex items-center">
    <input
      type="radio"
      name="filter"
      value="public_hats"
      checked={tempFilter === 'public_hats'}
      onChange={(e) => setTempFilter(e.target.value)}
      className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
    />
    <span className="ml-2 text-sm text-gray-700">Public VAs</span>
  </label>
</div>
                    </div>

                    {/* Connector filter -- narrows the list to VAs using a connector */}
                    {connectors.length > 0 && (
                      <div className="border-t pt-4">
                        <h4 className="flex items-center text-sm font-medium text-gray-700 mb-3">
                          <Settings2 size={16} className="mr-2 text-violet-500" />
                          Connectors
                        </h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="connectorFilter"
                              checked={tempConnectorFilter === null}
                              onChange={() => setTempConnectorFilter(null)}
                              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                            />
                            <span className="ml-2 text-sm text-gray-700">All Connectors</span>
                          </label>
                          {connectors.map((connector) => (
                            <label key={connector.id} className="flex items-center">
                              <input
                                type="radio"
                                name="connectorFilter"
                                checked={tempConnectorFilter === connector.id}
                                onChange={() => setTempConnectorFilter(connector.id)}
                                className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                              />
                              <span className="ml-2 text-sm text-gray-700 truncate">
                                {connector.name}
                              </span>
                              <span className="ml-2 text-xs text-gray-400 capitalize">
                                {connector.type}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex justify-between">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                      onClick={handleClearFilters}
                    >
                      Clear all filters
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                      onClick={handleApplyFilter}
                    >
                      Done
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default VAListView;